import { OktaAuth } from "@okta/okta-auth-js";
import { ParsedLocation } from "@tanstack/react-router";
import { RouterContext } from "../types/routerContext.ts";
import { getConfig } from "./runtimeConfig.ts";
import { Feature } from "../hooks/useFeatureFlag.ts";
import { createAuthorizationService, AuthorizationService } from "../services/authorizationService.ts";

const scopeDomainPrefix = 'aap-productbeheer-'

// Base scopes (non-environment-specific)
const baseScopes = [
    "openid",
    "profile",
    "email",
    "offline_access",
];

let oktaAuthInstance: OktaAuth | null = null;
let authorizationServiceInstance: AuthorizationService | null = null;

/**
 * Gets the complete list of Okta scopes including environment-specific API scope
 * Must be called after loadRuntimeConfig() has completed
 */
function getOktaScopes(): string[] {
    const config = getConfig();
    const apiScope = `${scopeDomainPrefix}api-${config.VITE_OKTA_ENV}`;
    
    console.log("[Auth] Constructed API scope:", apiScope);
    
    return [...baseScopes, apiScope];
}

/**
 * Initializes the OktaAuth instance with runtime configuration
 * Must be called after loadRuntimeConfig() has completed
 */
export function initializeOktaAuth(): OktaAuth {
    if (oktaAuthInstance) {
        return oktaAuthInstance;
    }

    const config = getConfig();
    const oktaScopes = getOktaScopes();

    if (!config.VITE_OKTA_ISSUER) {
        throw new Error(
            "VITE_OKTA_ISSUER is not configured. " +
            "Please ensure environment variables are properly set."
        );
    }

    if (!config.VITE_OKTA_CLIENT_ID) {
        throw new Error(
            "VITE_OKTA_CLIENT_ID is not configured. " +
            "Please ensure environment variables are properly set."
        );
    }

    console.log("[Auth] Initializing Okta with scopes:", oktaScopes);
    console.log("[Auth] VITE_OKTA_ENV:", config.VITE_OKTA_ENV);
    console.log("[Auth] Issuer:", config.VITE_OKTA_ISSUER);

    oktaAuthInstance = new OktaAuth({
        devMode: false,
        issuer: config.VITE_OKTA_ISSUER,
        clientId: config.VITE_OKTA_CLIENT_ID,
        redirectUri: `${window.location.origin}/callback`,
        scopes: oktaScopes,
        pkce: true,
        tokenManager: {
            // NOTE: sessionStorage is used instead of 'memory' to support Safari (ITP blocks silent
            // token renewal via iframes when tokens are in memory). This is a known XSS trade-off.
            // The proper fix is to configure a custom Okta domain (subdomain of our own domain) so
            // Safari treats auth as same-site. See docs/notes/safari-okta-itp.md for migration steps.
            storage: 'sessionStorage',
            autoRenew: true,            // Auto-refresh tokens before expiry
            expireEarlySeconds: 120,    // Refresh 2 minutes before expiry
        },
    });

    // Initialize authorization service
    authorizationServiceInstance = createAuthorizationService(oktaAuthInstance, oktaScopes);

    return oktaAuthInstance;
}

/**
 * Gets the initialized OktaAuth instance
 * Throws an error if not initialized yet
 */
export function getOktaAuth(): OktaAuth {
    if (!oktaAuthInstance) {
        throw new Error(
            "OktaAuth has not been initialized. " +
            "Call initializeOktaAuth() before accessing the auth instance."
        );
    }
    return oktaAuthInstance;
}

/**
 * Gets the initialized AuthorizationService instance
 * Throws an error if not initialized yet
 */
export function getAuthorizationService(): AuthorizationService {
    if (!authorizationServiceInstance) {
        throw new Error(
            "AuthorizationService has not been initialized. " +
            "Call initializeOktaAuth() before accessing the authorization service."
        );
    }
    return authorizationServiceInstance;
}

/**
 * Route guard that ensures user is authenticated and has required scope.
 * Redirects to login or no-permission page if checks fail.
 */
export async function ensureAuthenticated({
    context: { auth },
    location,
}: {
    context: RouterContext;
    location: ParsedLocation;
}): Promise<void> {
    const authService = getAuthorizationService();
    await authService.enforceAuthorization(location.href);
}

/**
 * Route guard that ensures user is authenticated, has required scope,
 * and has access to a specific feature.
 */
export const ensureAuthenticatedAndFeatureAccess =
  (feature: Feature) =>
  async ({
    context,
    location,
  }: {
    context: RouterContext
    location: ParsedLocation
  }) => {
    const authService = getAuthorizationService();
    await authService.enforceFeatureAccess(
      context.enabledFeatures,
      feature,
      location.href
    );
  }
